import {
	ResponsiveContainer,
	ComposedChart,
	XAxis,
	YAxis,
	Bar,
	Line,
	LabelList,
	Tooltip,
	BarChart,
	Legend,
	AreaChart,
	Area,
	Scatter,
} from "recharts";
import { numberFormatter } from "../../utils";
import moment from "moment";

interface Props {
	chartData: any;
	heading: any;
	page: any;
	isVisible: any;
}

interface T {
	quarter: string;
	actual: number;
	forecastedSpend: number;
	spendNeededForNextTier: number;
	spendShift: number;
	targetGrowthSpend: number;
}

const CustomLabel = (props: any) => {
	const { x, y, value: valuePrev, index } = props.payload;
	const { page, dataLength, data } = props;
	const d = 20; // Distance for positioning
	const shouldShow = index === 0 || index === dataLength - 1;
	if (!shouldShow) return null;
	// Get the corresponding current value
	const valueCurr = data[index]?.netCost_Curr || 0;
	// Determine position
	let lineY, textY;
	if (valuePrev >= valueCurr) {
		// Position above
		lineY = y - d;
		textY = y - d - 5;
	} else {
		// Position below
		lineY = y + d;
		textY = y + d + 5;
	}
	return (
		<g>
			<line
				x1={x}
				y1={y}
				x2={x}
				y2={lineY}
				stroke="#0097a9"
				opacity={0.5}
			/>
			<text
				x={x}
				y={textY}
				fill="#0097a9"
				className="graph-label-small"
				opacity={`${page == "summary" ? 0.8 : 1}`}
				textAnchor="middle"
			>
				{numberFormatter(valuePrev, 1, "$")}
			</text>
		</g>
	);
};

const CustomLabel2 = (props: any) => {
	const { x, y, value: valueCurr, index } = props.payload;
	const { page, dataLength, data } = props;
	const d = 20; // Distance for positioning
	const shouldShow = index === 0 || index === dataLength - 1;
	if (!shouldShow) return null;
	// Get the corresponding previous value
	const valuePrev = data[index]?.netCost_Prev || 0;
	// Determine position
	let lineY, textY;
	if (valueCurr > valuePrev) {
		// Position above
		lineY = y - d;
		textY = y - d - 5;
	} else {
		// Position below
		lineY = y + d;
		textY = y + d + 5;
	}
	return (
		<g>
			<line
				x1={x}
				y1={y}
				x2={x}
				y2={lineY}
				stroke="#86bc25"
				opacity={0.5}
			/>
			<text
				x={x}
				y={textY}
				fill="#86bc25"
				className="graph-label-small"
				opacity={`${page == "summary" ? 0.8 : 1}`}
				textAnchor="middle"
			>
				{numberFormatter(valueCurr, 1, "$")}
			</text>
		</g>
	);
};

const NetCostReductionChart = (props: Props) => {
	let data: any = props?.chartData?.finalData || [];
	let avgReductionValue: any = props?.chartData?.averageReduction || {};
	const { page } = props;
	let minValue = 0;
	let maxValue = 0;
	let newData: any = [];
	// Always initialize with empty array if data is not available
	if (data?.length > 0) {
		// Calculate min/max values for the YAxis domain
		const minPrev = data.reduce((min: any, obj: any) => {
			return obj.netCost_Prev < min ? obj.netCost_Prev : min;
		}, Infinity);
		const minCurr = data.reduce((min: any, obj: any) => {
			return obj.netCost_Curr < min ? obj.netCost_Curr : min;
		}, Infinity);
		const maxPrev = data.reduce((max: any, obj: any) => {
			return obj.netCost_Prev > max ? obj.netCost_Prev : max;
		}, -Infinity);
		const maxCurr = data.reduce((max: any, obj: any) => {
			return obj.netCost_Curr > max ? obj.netCost_Curr : max;
		}, -Infinity);
		minValue = minPrev > minCurr ? minCurr : minPrev;
		maxValue = maxPrev > maxCurr ? maxPrev : maxCurr;
		// Process each data point to add the range information
		newData = data
			.map((item: any, id: any) => {
				const newItem = { ...item };
				newItem.formattedWeek = moment.utc(item.month).format("MMM");
				if (item.monthCovered) {
					// Determine which value is larger for this point
					if (item.netCost_Prev > item.netCost_Curr) {
						// When 2024 cost is higher than 2025: green area
						newItem.greenRange = [
							item.netCost_Curr,
							item.netCost_Prev,
						];
						newItem.redRange = [
							item.netCost_Curr,
							item.netCost_Curr,
						]; // zero width
					} else if (item.netCost_Curr > item.netCost_Prev) {
						// When 2025 cost is higher than 2024: red area
						newItem.greenRange = [
							item.netCost_Curr,
							item.netCost_Curr,
						]; // zero width
						newItem.redRange = [
							item.netCost_Prev,
							item.netCost_Curr,
						];
					} else {
						// When costs are equal: no area
						newItem.greenRange = [
							item.netCost_Curr,
							item.netCost_Curr,
						];
						newItem.redRange = [
							item.netCost_Curr,
							item.netCost_Curr,
						];
					}
					return newItem;
				}
				return null;
			})
			.filter(Boolean); // Filter out any null entries from non-covered months
	}
	let heading = props?.heading;

	const footerData = [{ category: "2025" }, { category: "2026" }];

	const updateFill = (category: string) => {
		switch (category) {
			case "2025":
				return { color: "#0097a9", style: "solid" };
			case "2026":
				return { color: "#86bc25", style: "solid" };
			default:
				return { color: "black", style: "solid" };
		}
	};

	const updateLegend = (category: string) => {
		return category;
	};

	const customTooltip = ({ active, payload }: any) => {
		if (active && payload && payload.length) {
			const data = payload[0].payload;

			const popupStyle = {
				width: "12vw",
				background: "white",
				border: "1px solid #ccc",
				boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
				borderRadius: "1vh",
				cursor: "pointer",
				color: "black",
			};

			return (
				<div style={popupStyle}>
					{/* Header Row */}
					<div className="w-full flex justify-between h-[3vh] items-center bg-[#f0f4f8] py-[3vh] px-[1vh] textSmall">
						<div className="font-bold uppercase">Month</div>
						<div className="font-bold" style={{ color: "#3f3f3f" }}>
							{data.formattedWeek}
						</div>
					</div>
					{/* Data Rows */}
					<div className="h-fit p-[1vh] mt-[.5vh] mb-[.5vh]">
						<p
							className="label"
							style={{ padding: "0", margin: "0" }}
						>
							<label
								className="graph-label-small"
								style={{
									color: "#5a5a5a",
									fontWeight: "normal",
								}}
							>
								Total Cost 2026:{" "}
							</label>
							<label
								className="graph-label-small"
								style={{
									color: "#3f3f3f",
									fontWeight: "bold",
								}}
							>
								{numberFormatter(data.netCost_Curr, 2, "$")}
							</label>
						</p>
						<p
							className="label"
							style={{ padding: "0", margin: "0" }}
						>
							<label
								className="graph-label-small"
								style={{
									color: "#5a5a5a",
									fontWeight: "normal",
								}}
							>
								Total Cost 2025:{" "}
							</label>
							<label
								className="graph-label-small"
								style={{
									color: "#3f3f3f",
									fontWeight: "bold",
								}}
							>
								{numberFormatter(data.netCost_Prev, 2, "$")}
							</label>
						</p>
					</div>
				</div>
			);
		}
		return null;
	};

	return (
		<div className="w-full h-full px-3">
			{/* <div className="headSpace pb-2 !h-[10%]">
				<div className="headContent min-w-[12vw]">Net Cost ($)</div>
				<span
					className="bg-[#4d4d4d] w-full h-[0] flex justify-end px-[2vh] font-semibold text-[1.8vh]"
					style={{
						color:
							avgReductionValue?.cost > 0 ? "#40a108" : "#a10808",
					}}
				>
					{numberFormatter(avgReductionValue?.percentage * 100, 1)}%
					in Net Cost{" "}
					{avgReductionValue?.cost > 0 ? "Reductions" : "Increase"}
				</span>
			</div> */}
			<div className="headSpace !h-[6vh]">
				<span className="headContent">Net Cost ($)</span>
				<p
					className="flex justify-end px-[2vh] font-semibold textNormal mt-[0] text-[#40a108]"
					style={{
						color:
							avgReductionValue?.cost > 0 ? "#40a108" : "#a10808",
					}}
				>
					{numberFormatter(avgReductionValue?.percentage * 100, 1)}%
					in Net Cost{" "}
					{avgReductionValue?.cost > 0 ? "Reductions" : "Increase"}
				</p>
			</div>
			<ResponsiveContainer
				width="100%"
				height={props.isVisible ? "69%" : "77%"}
				className="bg-[#ffff] px-[1vh] "
			>
				<ComposedChart
					data={newData}
					margin={{
						top: 20,
						right: 30,
						left: 30,
					}}
				>
					<XAxis
						dataKey="formattedWeek"
						className="graph-label"
						stroke="black"
						interval={0}
					/>
					<YAxis
						hide
						domain={[
							(dataMin: any) => dataMin / 1.2,
							(dataMax: any) => dataMax * 1.1,
						]}
					/>

					{/* Green area for when 2024 is higher */}
					<Area
						dataKey="greenRange"
						stroke="#dce0a7"
						fill="#dce0a7"
						isAnimationActive={false}
					/>
					{/* Red area for when 2025 is higher */}
					<Area
						dataKey="redRange"
						stroke="#ffcccc"
						fill="#ffcccc"
						isAnimationActive={false}
					/>
					<Line
						type="linear"
						dataKey="netCost_Prev"
						stroke="#0097a9"
					name={`2025`}
						strokeDasharray={"0"}
						connectNulls={true}
						strokeWidth={3}
						dot={
							page == "summary"
								? ($) => (
										<CustomDot
											{...$}
											data={newData}
											color={"#0097a9"}
										/>
								  )
								: false
						}
					>
						<LabelList
							dataKey="netCost_Prev"
							content={($) => (
								<CustomLabel
									payload={$}
									page={page}
									dataLength={newData.length}
									data={newData}
								/>
							)}
						/>
					</Line>
					<Line
						type="linear"
						dataKey="netCost_Curr"
						name={`2025`}
						stroke="#86bc25"
						strokeDasharray={"0"}
						connectNulls={false}
						strokeWidth={3}
						dot={
							page == "summary"
								? ($) => (
										<CustomDot
											{...$}
											data={newData}
											color={"#86bc25"}
										/>
								  )
								: false
						}
					>
						<LabelList
							dataKey="netCost_Curr"
							content={($) => (
								<CustomLabel2
									payload={$}
									page={page}
									dataLength={newData.length}
									data={newData}
								/>
							)}
						/>
					</Line>
					<Tooltip
						contentStyle={{
							color: "white",
							backgroundColor: "rgb(26, 25, 22)",
						}}
						cursor={{ strokeDasharray: "3 3", fill: "#212121" }}
						content={customTooltip}
					/>
				</ComposedChart>
			</ResponsiveContainer>
			<div className="contentFooter h-[12vh]">
				<div className="flex flex-wrap items-start gap-x-2 gap-y-2 p-4 w-full">
					{footerData.map((entry, index) => {
						const { color, style } = updateFill(entry.category);
						return (
							<div
								key={`legend-${index}`}
								className="flex items-center min-h-[20px] cursor-pointer"
							>
								{style === "solid" && (
									<div
										className="w-3 h-3 mr-2"
										style={{ backgroundColor: color }}
									></div>
								)}
								<span
									className="text-[0.85em] leading-tight"
									style={{ color }}
								>
									{updateLegend(entry.category)}
								</span>
							</div>
						);
					})}
				</div>
			</div>
			<p className="bg-[#4d4d4d] w-full h-[0] flex justify-end px-[2vh] italic text-[1.8vh] font-normal relative top-[-3vh] text-black">
				Only includes common SKUs
			</p>
		</div>
	);
};

const CustomLegend = (props: any) => {
	const { payload } = props.payload;
	return (
		<ul className="custom-legend flex justify-start">
			{payload.map((entry: any, index: any) => (
				<>
					{entry?.value !== "range" &&
						entry?.value !== "dotValue" &&
						entry?.value !== "greenRange" &&
						entry?.value !== "redRange" && (
							<li
								key={`item-${index}`}
								style={{
									color:
										entry.value === "2025"
											? "#86bc25"
											: "#0097a9",
									marginLeft: 5,
									fontSize: "1.8vh",
								}}
							>
								<span
									style={{
										backgroundColor:
											entry.value === "2025"
												? "#86bc25"
												: "#0097a9",
										width: "1.3vh",
										height: 2,
										display: "inline-block",
										marginRight: 5,
										top: -4,
										position: "relative",
									}}
								/>
								{entry.value}
							</li>
						)}
				</>
			))}
		</ul>
	);
};

const CustomDot = (props: any) => {
	const { cx, cy, index, data, color } = props;
	// Only render for first and last point
	if (index === 0 || index === data.length - 1) {
		return <circle cx={cx} cy={cy} r={5} fill={color} stroke="none" />;
	}
	return null;
};

export default NetCostReductionChart;
